import React from "react";
import { FileText, Shield, Hash, Building2, User, Layers, Tag, RotateCcw, Sparkles } from "lucide-react";
import type { SowFields } from "../../../../../utils/api/endpoints/sow";

interface CoverMetaEditorProps {
  fields: SowFields;
  setField: <K extends keyof SowFields>(key: K, value: SowFields[K]) => void;
  clientName: string;
  sowVersion: number;
}

export const CoverMetaEditor: React.FC<CoverMetaEditorProps> = ({
  fields,
  setField,
  clientName,
  sowVersion
}) => {
  const today = new Date().toLocaleDateString("en-NZ", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
  const defaultDocRef = `055-${today.replace(/\s+/g, "").toUpperCase()}-CTB-SOW-V${sowVersion || 1}`;

  // Calculate auto subtitle from targets
  const autoSubtitle = Array.from(
    new Set(
      (fields.targetItems || [])
        .map((m) => m.assetName || m.assessmentType)
        .filter(Boolean)
    )
  ).join(" & ").toUpperCase() || "PENETRATION TESTING";

  const handleAutoGenerateSubtitle = () => {
    setField("coverSubtitle", autoSubtitle);
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <FileText size={16} className="text-ctb-blue" />
          Cover Page & Document Metadata
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Customize every line on the PDF cover page, including headers, document titles, scoped asset subtitles, classification, and document identifiers.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        {/* Engagement Type / Header Category */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between h-5">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Tag size={13} className="text-slate-400" />
              Category Header
            </label>
            <button
              type="button"
              onClick={() => setField("coverEngagementType", "PENETRATION TESTING ENGAGEMENT")}
              className="text-[10px] text-slate-400 hover:text-ctb-blue flex items-center gap-1 cursor-pointer"
              title="Reset to default"
            >
              <RotateCcw size={10} /> Reset
            </button>
          </div>
          <input
            type="text"
            value={fields.coverEngagementType ?? ""}
            onChange={(e) => setField("coverEngagementType", e.target.value)}
            className="w-full text-xs font-semibold uppercase tracking-wider bg-white border border-slate-200 focus:border-ctb-blue focus:ring-1 focus:ring-ctb-blue/20 rounded-lg px-3 py-2 text-ctb-blue"
            placeholder="PENETRATION TESTING ENGAGEMENT"
          />
          <span className="text-[10px] text-slate-400 block">Header tag banner displayed above main title on cover page.</span>
        </div>

        {/* Document Title */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between h-5">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <FileText size={13} className="text-slate-400" />
              Main Document Title
            </label>
            <button
              type="button"
              onClick={() => setField("coverDocumentTitle", "Statement of Work")}
              className="text-[10px] text-slate-400 hover:text-ctb-blue flex items-center gap-1 cursor-pointer"
              title="Reset to default"
            >
              <RotateCcw size={10} /> Reset
            </button>
          </div>
          <input
            type="text"
            value={fields.coverDocumentTitle ?? ""}
            onChange={(e) => setField("coverDocumentTitle", e.target.value)}
            className="w-full text-xs font-bold bg-white border border-slate-200 focus:border-ctb-blue focus:ring-1 focus:ring-ctb-blue/20 rounded-lg px-3 py-2 text-slate-900"
            placeholder="Statement of Work"
          />
          <span className="text-[10px] text-slate-400 block">Primary headline title (e.g., Statement of Work).</span>
        </div>
      </div>

      {/* Cover Subtitle / Scoped Targets Highlight */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <Layers size={13} className="text-slate-400" />
            Cover Subtitle / Target Assets Banner
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAutoGenerateSubtitle}
              className="text-[10px] text-ctb-blue hover:text-blue-800 font-semibold flex items-center gap-1 cursor-pointer bg-blue-50 px-2 py-0.5 rounded border border-blue-100"
            >
              <Sparkles size={10} /> Auto-Generate from Scoped Assets
            </button>
            <button
              type="button"
              onClick={() => setField("coverSubtitle", "")}
              className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-1 cursor-pointer"
            >
              <RotateCcw size={10} /> Clear
            </button>
          </div>
        </div>
        <textarea
          rows={2}
          value={fields.coverSubtitle !== undefined && fields.coverSubtitle !== null ? fields.coverSubtitle : autoSubtitle}
          onChange={(e) => setField("coverSubtitle", e.target.value)}
          className="w-full text-xs font-medium uppercase bg-white border border-slate-200 focus:border-ctb-blue focus:ring-1 focus:ring-ctb-blue/20 rounded-lg p-2.5 text-slate-700 leading-relaxed"
          placeholder={autoSubtitle || "E.g. WEB APPLICATION & CLOUD INFRASTRUCTURE"}
        />
        <span className="text-[10px] text-slate-400 block">
          Subtitle on cover page. Defaults to clean, deduplicated in-scope target names.
        </span>
      </div>

      {/* Metadata Table Fields */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
        <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
          <Shield size={14} className="text-ctb-blue" />
          Cover Metadata Table
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Prepared For */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <User size={13} className="text-slate-400" />
              Prepared for:
            </label>
            <input
              type="text"
              value={fields.coverPreparedFor ?? ""}
              onChange={(e) => setField("coverPreparedFor", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 focus:border-ctb-blue focus:ring-1 focus:ring-ctb-blue/20 rounded-lg px-3 py-2 text-slate-800"
              placeholder={clientName || "Client Company Name"}
            />
          </div>

          {/* Prepared By */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Building2 size={13} className="text-slate-400" />
              Prepared by:
            </label>
            <input
              type="text"
              value={fields.coverPreparedBy ?? ""}
              onChange={(e) => setField("coverPreparedBy", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 focus:border-ctb-blue focus:ring-1 focus:ring-ctb-blue/20 rounded-lg px-3 py-2 text-slate-800"
              placeholder="Capture The Bug Limited"
            />
          </div>

          {/* Document Reference */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Hash size={13} className="text-slate-400" />
                Document Reference:
              </label>
              <button
                type="button"
                onClick={() => setField("coverDocumentReference", defaultDocRef)}
                className="text-[10px] text-slate-400 hover:text-ctb-blue flex items-center gap-1 cursor-pointer"
              >
                <RotateCcw size={10} /> Auto
              </button>
            </div>
            <input
              type="text"
              value={fields.coverDocumentReference ?? ""}
              onChange={(e) => setField("coverDocumentReference", e.target.value)}
              className="w-full text-xs font-mono bg-white border border-slate-200 focus:border-ctb-blue focus:ring-1 focus:ring-ctb-blue/20 rounded-lg px-3 py-2 text-slate-800"
              placeholder={defaultDocRef}
            />
          </div>

          {/* Version */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Tag size={13} className="text-slate-400" />
              Version:
            </label>
            <input
              type="text"
              value={fields.coverVersionLabel ?? ""}
              onChange={(e) => setField("coverVersionLabel", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 focus:border-ctb-blue focus:ring-1 focus:ring-ctb-blue/20 rounded-lg px-3 py-2 text-slate-800"
              placeholder={String(sowVersion || "1.0")}
            />
          </div>

          {/* Classification */}
          {(() => {
            const currentClassification = fields.coverClassification || "Confidential";
            const docTitle = fields.coverDocumentTitle || "Statement of Work";
            const docRef = fields.coverDocumentReference || defaultDocRef;
            return (
              <>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <Shield size={13} className="text-emerald-600" />
                    Classification:
                  </label>
                  <select
                    value={fields.coverClassification ?? "Confidential"}
                    onChange={(e) => {
                      const newClass = e.target.value;
                      setField("coverClassification", newClass);
                      const replaceClass = (str?: string) =>
                        str ? str.replace(/Confidential|Restricted|Commercial In Confidence|Internal Use Only|Public/gi, newClass) : str;

                      if (fields.coverFooterNote) {
                        setField("coverFooterNote", replaceClass(fields.coverFooterNote));
                      }
                      if (fields.innerPageHeaderText) {
                        setField("innerPageHeaderText", replaceClass(fields.innerPageHeaderText));
                      }
                      if (fields.footerReferenceNote) {
                        setField("footerReferenceNote", replaceClass(fields.footerReferenceNote));
                      }
                    }}
                    className="w-full text-xs font-bold bg-white border border-slate-200 focus:border-ctb-blue focus:ring-1 focus:ring-ctb-blue/20 rounded-lg px-3 py-2 text-slate-800 cursor-pointer"
                  >
                    <option value="Confidential">Confidential</option>
                    <option value="Commercial In Confidence">Commercial In Confidence</option>
                    <option value="Restricted">Restricted</option>
                    <option value="Internal Use Only">Internal Use Only</option>
                    <option value="Public">Public</option>
                  </select>
                </div>

                {/* Cover Footer Note */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700">Cover Bottom Footer Note:</label>
                  <input
                    type="text"
                    value={fields.coverFooterNote ?? ""}
                    onChange={(e) => setField("coverFooterNote", e.target.value)}
                    className="w-full text-xs bg-white border border-slate-200 focus:border-ctb-blue focus:ring-1 focus:ring-ctb-blue/20 rounded-lg px-3 py-2 text-slate-600"
                    placeholder={`capturethebug.xyz   ·   ${currentClassification}: intended recipient only`}
                  />
                </div>

                {/* Inner Pages Header Text */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700">Inner Pages Header (Top Right):</label>
                  <input
                    type="text"
                    value={fields.innerPageHeaderText ?? ""}
                    onChange={(e) => setField("innerPageHeaderText", e.target.value)}
                    className="w-full text-xs bg-white border border-slate-200 focus:border-ctb-blue focus:ring-1 focus:ring-ctb-blue/20 rounded-lg px-3 py-2 text-slate-800"
                    placeholder={`${docTitle}   ·   ${currentClassification}`}
                  />
                </div>

                {/* Inner Pages Footer Reference Note */}
                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-xs font-semibold text-slate-700">Inner Pages Footer Reference Note (Bottom Left):</label>
                  <input
                    type="text"
                    value={fields.footerReferenceNote ?? ""}
                    onChange={(e) => setField("footerReferenceNote", e.target.value)}
                    className="w-full text-xs font-mono bg-white border border-slate-200 focus:border-ctb-blue focus:ring-1 focus:ring-ctb-blue/20 rounded-lg px-3 py-2 text-slate-600"
                    placeholder={`${docRef}   ·   ${currentClassification}`}
                  />
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};
